import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { exchangeYandexVerify } from "../api/auth";
import { finishLoginWithReturn } from "../api/playTicket";
import logo from "../assets/logo.png";

/**
 * Страница куда Яндекс редиректит после авторизации.
 * URL: /auth/yandex/callback?code=...&state=...
 *
 * Два режима (определяется по localStorage.yandex_oauth_mode):
 *   1. login (по умолчанию): обычный вход. loginWithOAuth → /app.
 *   2. verify: подтверждение текущего залогиненного аккаунта через Яндекс.
 *      Если email не совпал → диалог «заменить email?».
 */
export default function YandexCallbackPage() {
  const navigate = useNavigate();
  const { loginWithOAuth } = useAuth();
  const [error, setError] = useState("");
  const [step, setStep] = useState("Подключаемся к Яндексу...");
  const [needsConfirmDialog, setNeedsConfirmDialog] = useState(null);
  // Guard от двойного вызова exchange в React StrictMode (useEffect срабатывает
  // дважды). state одноразовый — второй вызов получит 400 «state истёк» и
  // перетрёт успешный step ошибкой, хотя verify уже прошёл.
  const exchangedRef = useRef(false);

  const handleVerify = async (code, state, confirmEmailChange = false, confirmToken = null) => {
    setStep(confirmEmailChange ? "Меняем email..." : "Проверяем Яндекс-аккаунт...");
    const res = await exchangeYandexVerify({ code, state, confirmEmailChange, confirmToken });
    if (!res.ok) {
      setError(res.message || "Не удалось подтвердить");
      return;
    }
    if (res.needsEmailChangeConfirmation) {
      setNeedsConfirmDialog({
        code, state,
        confirmToken: res.confirmToken,   // для второго запроса (code/state одноразовые)
        currentEmail: res.currentEmail,
        yandexEmail: res.yandexEmail,
        message: res.message,
      });
      return;
    }
    if (res.verified) {
      setStep("Готово! Email подтверждён.");
      setTimeout(() => navigate("/app", { replace: true }), 800);
      return;
    }
    setError("Неожиданный ответ от сервера");
  };

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const yandexError = params.get("error");

    if (yandexError) {
      setError("Яндекс отменил вход: " + yandexError);
      return;
    }
    if (!code || !state) {
      setError("Неполные данные от Яндекса");
      return;
    }

    const mode = localStorage.getItem("yandex_oauth_mode") || "login";
    localStorage.removeItem("yandex_oauth_mode");

    (async () => {
      try {
        if (mode === "verify") {
          await handleVerify(code, state, false);
        } else {
          setStep("Получаем ваш профиль...");
          await loginWithOAuth({ provider: "yandex", code, state });
          // Возврат на инвайт-ссылку (если входили по ней) — иначе в кабинет.
          // Перед уходом на Яндекс мы сохранили ?return в localStorage.
          setStep("Заходим...");
          const redirected = await finishLoginWithReturn(
            () => navigate("/app", { replace: true })
          );
          if (redirected) setStep("Открываем редактор...");
        }
      } catch (e) {
        setError(e?.message || "Не удалось войти через Яндекс");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Диалог «заменить email на Яндекс-адрес?»
  if (needsConfirmDialog) {
    return (
      <Layout>
        <div style={{
          minHeight: "60vh", display: "flex", alignItems: "center",
          justifyContent: "center", padding: 24,
        }}>
          <div style={{
            maxWidth: 480, background: "#fff",
            border: "1px solid #E8EBF1", borderRadius: 14,
            padding: "24px 26px", boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          }}>
            <h2 style={{ margin: "0 0 12px", fontSize: 22 }}>
              Подтвердить смену email?
            </h2>
            <p style={{ margin: "0 0 14px", color: "#3D4148", fontSize: 14, lineHeight: 1.5 }}>
              {needsConfirmDialog.message}
            </p>
            <div style={{
              background: "#F5F6FA", borderRadius: 10,
              padding: "12px 14px", marginBottom: 18,
              fontSize: 14, lineHeight: 1.7,
            }}>
              <div>
                <span style={{ color: "#60646A" }}>Сейчас:</span>{" "}
                <b>{needsConfirmDialog.currentEmail}</b>
              </div>
              <div>
                <span style={{ color: "#60646A" }}>Станет:</span>{" "}
                <b style={{ color: "#3357FF" }}>{needsConfirmDialog.yandexEmail}</b>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={async () => {
                  setNeedsConfirmDialog(null);
                  await handleVerify(
                    needsConfirmDialog.code,
                    needsConfirmDialog.state,
                    true,
                    needsConfirmDialog.confirmToken,
                  );
                }}
                style={{
                  flex: 1, height: 44, borderRadius: 10,
                  background: "#3357FF", color: "#fff",
                  border: "none", fontSize: 14, fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Да, заменить
              </button>
              <button
                type="button"
                onClick={() => navigate("/app")}
                style={{
                  flex: 1, height: 44, borderRadius: 10,
                  background: "#fff", color: "#222831",
                  border: "1.5px solid #D8DBE3",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
      }}>
        <img src={logo} alt="Рублокс" style={{ width: 64, height: 64, marginBottom: 18 }} />
        {!error ? (
          <>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: "4px solid #E8EBF1",
              borderTopColor: "#FC3F1D",
              animation: "rb-yc-spin 1s linear infinite",
              marginBottom: 16,
            }} />
            <style>{`@keyframes rb-yc-spin { to { transform: rotate(360deg); } }`}</style>
            <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>Вход через Яндекс</h2>
            <p style={{ margin: 0, color: "#60646A", fontSize: 15 }}>{step}</p>
          </>
        ) : (
          <>
            <div style={{ fontSize: 48, marginBottom: 8 }}>⚠</div>
            <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>Не удалось войти</h2>
            <p style={{ margin: "0 0 18px", color: "#60646A", fontSize: 14, maxWidth: 420 }}>
              {error}
            </p>
            <button
              type="button"
              onClick={() => navigate("/app")}
              className="rb-btn rb-btn--primary"
              style={{ padding: "10px 24px" }}
            >
              В кабинет
            </button>
          </>
        )}
      </div>
    </Layout>
  );
}
